import React from 'react';
import type { LayoutVariant } from '@/hooks/usePageDesign';

interface ConfigurableLayoutProps {
  layout: LayoutVariant;
  main: React.ReactNode;
  sidebar?: React.ReactNode;
  className?: string;
}

/**
 * Responsive layout wrapper that handles sidebar position based on admin config.
 * - sidebar_right: main content left, sidebar right (default)
 * - sidebar_left: sidebar left, main content right
 * - full_width: no sidebar, full-width content
 */
export const ConfigurableLayout: React.FC<ConfigurableLayoutProps> = ({
  layout,
  main,
  sidebar,
  className = '',
}) => {
  if (layout === 'full_width' || !sidebar) {
    return (
      <div className={`container mx-auto px-4 py-16 ${className}`}>
        <div className="max-w-4xl mx-auto space-y-12">
          {main}
        </div>
      </div>
    );
  }

  const sidebarContent = (
    <aside className="space-y-6">
      <div className="sticky top-24 space-y-6">
        {sidebar}
      </div>
    </aside>
  );

  return (
    <div className={`container mx-auto px-4 py-16 ${className}`}>
      <div className="grid lg:grid-cols-3 gap-12">
        {layout === 'sidebar_left' ? (
          <>
            {sidebarContent}
            <div className="lg:col-span-2 space-y-12">{main}</div>
          </>
        ) : (
          <>
            <div className="lg:col-span-2 space-y-12">{main}</div>
            {sidebarContent}
          </>
        )}
      </div>
    </div>
  );
};
